import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import { useRedeemCode, type RedeemedLicense } from "@/pages/licenses/hooks/useRedeemCode";

interface RedeemPanelProps {
  onRedeemed: () => void;
}

// 「核销激活码」入口：客户填入分发到手的原始裸码，即可自助换取一张专属许可证。
export default function RedeemPanel({ onRedeemed }: RedeemPanelProps) {
  const { t } = useTranslation();
  const { edition } = useLicenseLabels();
  const { redeem, submitting } = useRedeemCode();
  const [code, setCode] = useState("");
  const [success, setSuccess] = useState<RedeemedLicense | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRedeem = async () => {
    const value = code.trim();
    if (!value) {
      setError(t("licenses.redeemEmptyCode"));
      setSuccess(null);
      return;
    }
    setError(null);
    try {
      const license = await redeem(value);
      setSuccess(license);
      setCode("");
      onRedeemed();
    } catch (err) {
      setSuccess(null);
      setError(err instanceof Error ? err.message : t("licenses.redeemFailed"));
    }
  };

  return (
    <div className="mb-8 rounded-lg border border-background-200 bg-secondary-50 p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-gift-2-line text-xl" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("licenses.redeemTitle")}</h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">{t("licenses.redeemDesc")}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-[11px] font-medium text-secondary-900">
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-shield-check-line" />
          </span>
          {t("licenses.redeemOnce")}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-key-2-line" />
          </span>
          <input
            type="text"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleRedeem();
            }}
            placeholder={t("licenses.redeemPlaceholder")}
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 font-mono text-sm uppercase tracking-wider text-foreground-900 placeholder:font-sans placeholder:tracking-normal placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <button
          type="button"
          disabled={submitting}
          onClick={() => void handleRedeem()}
          className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-check-double-line"} />
          </span>
          {submitting ? t("licenses.redeemSubmitting") : t("licenses.redeemSubmit")}
        </button>
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-xs text-accent-800">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {error}
        </div>
      )}

      {success && (
        <div className="mt-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-checkbox-circle-line" />
            </span>
            {t("licenses.redeemSuccessTitle")}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-foreground-600">
            <span>
              {t("licenses.redeemProduct")}：
              <span className="font-medium text-foreground-900">{success.product_name}</span>
            </span>
            <span>
              {t("licenses.redeemEdition")}：
              <span className="font-medium text-foreground-900">{edition(success.edition)}</span>
            </span>
            <span>
              {t("licenses.redeemSeats")}：
              <span className="font-medium text-foreground-900">{success.seat_count}</span>
            </span>
            <span className="font-mono text-foreground-900">{success.license_key}</span>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-start gap-2 rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5">
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
          <i className="ri-information-line" />
        </span>
        <p className="text-[11px] leading-relaxed text-foreground-500">{t("licenses.redeemTip")}</p>
      </div>
    </div>
  );
}